import { z } from 'zod';
import {
  AUTOPILOT_LEVELS,
  CONTENT_GOALS,
  CONTENT_STATUSES,
  CONTENT_TYPES,
  LANGUAGES,
  PLATFORMS,
  PROMPT_CATEGORIES,
  PUBLISHER_TYPES,
  SOURCE_TYPES,
} from '@kontentowo/config';

export const idSchema = z.string().min(1).max(64);

const isoDate = z.string().datetime({ offset: true });

export const brandCreateSchema = z.object({
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .regex(/^[a-z0-9-]+$/, 'slug: lowercase letters, digits and dashes only')
    .max(80)
    .optional(),
  description: z.string().max(4000).nullable().optional(),
  website: z.string().url().max(400).nullable().optional(),
  language: z.enum(LANGUAGES).default('pl'),
  additionalLanguages: z.array(z.enum(LANGUAGES)).default([]),
  toneOfVoice: z.string().max(2000).nullable().optional(),
  targetAudience: z.string().max(2000).nullable().optional(),
  defaultCTA: z.string().max(500).nullable().optional(),
  brandPrompt: z.string().max(8000).nullable().optional(),
  visualPrompt: z.string().max(8000).nullable().optional(),
  colors: z.array(z.string().max(50)).max(20).nullable().optional(),
  logoAssetId: idSchema.nullable().optional(),
  autopilotLevel: z.enum(AUTOPILOT_LEVELS).default('OFF'),
  enabled: z.boolean().default(true),
  publishConfig: z
    .object({
      weeklyGoal: z.number().int().min(0).nullable().default(null),
      defaultPlatforms: z.array(z.enum(PLATFORMS)).default([]),
      defaultHashtags: z.array(z.string().max(100)).default([]),
      requireApproval: z.boolean().default(true),
      similarityThreshold: z.number().min(0).max(1).default(0.86),
      defaultPublisherType: z.enum(PUBLISHER_TYPES).default('SOCIAMONIALS'),
      sociamonialsWorkspaceId: z.string().max(50).nullable().default(null),
      telegramChannelId: z.string().max(100).nullable().default(null),
      ctaTranslations: z.record(z.string(), z.string()).default({}),
    })
    .default({}),
});

export const brandUpdateSchema = brandCreateSchema.partial();

export const contentCreateSchema = z.object({
  brandId: idSchema,
  campaignId: idSchema.nullable().optional(),
  ideaId: idSchema.nullable().optional(),
  topic: z.string().min(1).max(300),
  brief: z.string().max(8000).nullable().optional(),
  goal: z.enum(CONTENT_GOALS).nullable().optional(),
  language: z.enum(LANGUAGES).default('pl'),
  contentType: z.enum(CONTENT_TYPES).default('post'),
});

export const generateSchema = z.object({
  brandId: idSchema,
  topic: z.string().min(1).max(300),
  goal: z.enum(CONTENT_GOALS).default('ACQUISITION'),
  language: z.enum(LANGUAGES).default('pl'),
  platforms: z.array(z.enum(PLATFORMS)).min(1),
  contentType: z.enum(CONTENT_TYPES).default('post'),
  campaignId: idSchema.nullable().optional(),
  ideaId: idSchema.nullable().optional(),
  brief: z.string().max(8000).nullable().optional(),
  asDraft: z.boolean().default(false),
});

export const scheduleSchema = z.object({
  scheduledAt: isoDate,
  timezone: z.string().default('Europe/Warsaw'),
  variantId: idSchema.optional(),
});

export const variantEditSchema = z.object({
  body: z.string().min(1).max(12000).optional(),
  title: z.string().max(500).nullable().optional(),
  hook: z.string().max(2000).nullable().optional(),
  hashtags: z.array(z.string().max(100)).max(60).optional(),
  cta: z.string().max(500).nullable().optional(),
  link: z.string().url().max(800).nullable().optional(),
});

export const socialAccountCreateSchema = z.object({
  brandId: idSchema,
  platform: z.enum(PLATFORMS),
  name: z.string().min(1).max(120),
  externalId: z.string().max(200).nullable().optional(),
  username: z.string().max(200).nullable().optional(),
  publisherType: z.enum(PUBLISHER_TYPES).default('SOCIAMONIALS'),
  profileRef: z.string().max(300).nullable().optional(),
  workspaceId: z.string().max(50).nullable().optional(),
  enabled: z.boolean().default(true),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const sourceCreateSchema = z.object({
  brandId: idSchema,
  type: z.enum(SOURCE_TYPES),
  name: z.string().min(1).max(200),
  url: z.string().url().max(800).nullable().optional(),
  config: z.record(z.string(), z.unknown()).default({}),
  enabled: z.boolean().default(true),
});

export const campaignCreateSchema = z.object({
  brandId: idSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  goal: z.enum(CONTENT_GOALS).nullable().optional(),
  startDate: z.string().datetime({ offset: true }).nullable().optional(),
  endDate: z.string().datetime({ offset: true }).nullable().optional(),
  status: z.enum(['PLANNED', 'ACTIVE', 'PAUSED', 'COMPLETED', 'ARCHIVED']).default('PLANNED'),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const campaignUpdateSchema = campaignCreateSchema.partial();

export const ideaCreateSchema = z.object({
  brandId: idSchema,
  campaignId: idSchema.nullable().optional(),
  topic: z.string().min(1).max(400),
  description: z.string().max(4000).nullable().optional(),
  goal: z.enum(CONTENT_GOALS).nullable().optional(),
  sourceId: idSchema.nullable().optional(),
  status: z.enum(['OPEN', 'PICKED', 'USED', 'DISCARDED']).default('OPEN'),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const ideaUpdateSchema = ideaCreateSchema.partial();

export const promptCreateSchema = z.object({
  name: z.string().min(1).max(200),
  slug: z.string().regex(/^[a-z0-9-]+$/).max(120),
  category: z.enum(PROMPT_CATEGORIES),
  version: z.number().int().default(1),
  systemPrompt: z.string().min(1).max(20000),
  userTemplate: z.string().min(1).max(20000),
  outputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
  enabled: z.boolean().default(true),
});

export const promptUpdateSchema = promptCreateSchema.partial();

export const settingUpdateSchema = z.object({
  value: z.string().max(20000),
});

export const providerConfigSchema = z.object({
  key: z.string().min(1).max(80),
  enabled: z.boolean().default(true),
  priority: z.number().int().default(10),
  config: z.record(z.string(), z.unknown()).default({}),
});

export const statusUpdateSchema = z.object({
  status: z.enum(CONTENT_STATUSES),
  note: z.string().max(2000).nullable().optional(),
});

export const reviewActionSchema = z.object({
  approved: z.boolean(),
  note: z.string().max(2000).nullable().optional(),
});

export type BrandCreate = z.infer<typeof brandCreateSchema>;
export type BrandUpdate = z.infer<typeof brandUpdateSchema>;
export type ContentCreate = z.infer<typeof contentCreateSchema>;
export type GenerateRequest = z.infer<typeof generateSchema>;
export type ScheduleRequest = z.infer<typeof scheduleSchema>;
export type VariantEdit = z.infer<typeof variantEditSchema>;
export type SocialAccountCreate = z.infer<typeof socialAccountCreateSchema>;
export type SourceCreate = z.infer<typeof sourceCreateSchema>;
export type PromptCreate = z.infer<typeof promptCreateSchema>;
export type PromptUpdate = z.infer<typeof promptUpdateSchema>;
export type SettingUpdate = z.infer<typeof settingUpdateSchema>;
export type CampaignCreate = z.infer<typeof campaignCreateSchema>;
export type CampaignUpdate = z.infer<typeof campaignUpdateSchema>;
export type IdeaCreate = z.infer<typeof ideaCreateSchema>;
export type IdeaUpdate = z.infer<typeof ideaUpdateSchema>;
export type ProviderConfig = z.infer<typeof providerConfigSchema>;
export type StatusUpdate = z.infer<typeof statusUpdateSchema>;
export type ReviewAction = z.infer<typeof reviewActionSchema>;